import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  UserPlus,
  Save,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  DollarSign,
  Link
} from 'lucide-react';
import { agentApi } from '@/services/agentApi';
import { useAuth } from '@/hooks/useAuth';

interface SubAccount {
  id: number;
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  agencyName: string;
  isActive: boolean;
  emailVerified: boolean;
  canAccessCommissions?: boolean;
  canAccessPartnerships?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const SubAccountsPage: React.FC = () => {
  const { user } = useAuth();
  const mainAgentAgencyName = user?.agentProfile?.agencyName || '';
  
  const [subAccounts, setSubAccounts] = useState<SubAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<SubAccount | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    canAccessCommissions: false,
    canAccessPartnerships: false,
  });

  const [editForm, setEditForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    password: '',
    confirmPassword: '',
    isActive: true,
    canAccessCommissions: false,
    canAccessPartnerships: false,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchSubAccounts();
  }, []);

  const fetchSubAccounts = async () => {
    try {
      setLoading(true);
      const data = await agentApi.getSubAccounts();
      setSubAccounts(data);
    } catch (error: any) {
      console.error('Error fetching sub-accounts:', error);
      toast.error(error.response?.data?.error || 'Failed to fetch sub-accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setCreateForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateCreateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!createForm.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!createForm.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (createForm.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!createForm.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!createForm.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!createForm.phone.trim()) newErrors.phone = 'WhatsApp number is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateCreateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await agentApi.createSubAccount(createForm);
      toast.success('Sub-account created successfully');
      setShowCreateModal(false);
      setCreateForm({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        canAccessCommissions: false,
        canAccessPartnerships: false,
      });
      fetchSubAccounts();
    } catch (error: any) {
      console.error('Error creating sub-account:', error);
      toast.error(error.response?.data?.error || 'Failed to create sub-account');
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateEditForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!editForm.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!editForm.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!editForm.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Password validation (optional - only validate if provided)
    if (editForm.password.trim()) {
      if (editForm.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      } else if (editForm.password !== editForm.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    } else if (editForm.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please enter password to confirm';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditClick = (account: SubAccount) => {
    setEditingAccount(account);
      setEditForm({
        email: account.email,
        firstName: account.firstName,
        lastName: account.lastName,
        phone: account.phone || '',
        password: '',
        confirmPassword: '',
        isActive: account.isActive,
        canAccessCommissions: account.canAccessCommissions || false,
        canAccessPartnerships: account.canAccessPartnerships || false,
      });
    setShowEditModal(true);
    setErrors({});
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingAccount) return;

    if (!validateEditForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      // Prepare update data - only include password if provided
      const updateData: any = {
        email: editForm.email,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        phone: editForm.phone,
        isActive: editForm.isActive,
        canAccessCommissions: editForm.canAccessCommissions,
        canAccessPartnerships: editForm.canAccessPartnerships,
      };
      
      // Only include password if it was provided
      if (editForm.password.trim()) {
        updateData.password = editForm.password;
      }
      
      await agentApi.updateSubAccount(editingAccount.id, updateData);
      toast.success('Sub-account updated successfully');
      setShowEditModal(false);
      setEditingAccount(null);
      fetchSubAccounts();
    } catch (error: any) {
      console.error('Error updating sub-account:', error);
      toast.error(error.response?.data?.error || 'Failed to update sub-account');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to deactivate this sub-account? They will no longer be able to access the portal.')) {
      return;
    }

    try {
      await agentApi.deleteSubAccount(id);
      toast.success('Sub-account deactivated successfully');
      fetchSubAccounts();
    } catch (error: any) {
      console.error('Error deleting sub-account:', error);
      toast.error(error.response?.data?.error || 'Failed to deactivate sub-account');
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h1 className="h3 fw-bold text-dark mb-1">Sub-Accounts (Counsellors)</h1>
              <p className="text-muted mb-0">Manage your team members who can create and submit applications</p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={18} className="me-2" />
              Add Sub-Account
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : subAccounts.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card border-0 shadow-sm"
        >
          <div className="card-body text-center py-5">
            <Users size={48} className="text-muted mb-3" />
            <h5 className="text-muted">No sub-accounts yet</h5>
            <p className="text-muted mb-4">Create your first sub-account to get started</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus size={18} className="me-2" />
              Add Sub-Account
            </button>
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card border-0 shadow-sm"
        >
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Agency</th>
                    <th>Permissions</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subAccounts.map((account) => (
                    <tr key={account.id}>
                      <td>
                        <div className="fw-semibold">
                          {account.firstName} {account.lastName}
                        </div>
                        {account.phone && (
                          <small className="text-muted">{account.phone}</small>
                        )}
                      </td>
                      <td>{account.email}</td>
                      <td>{account.agencyName}</td>
                      <td>
                        <div className="d-flex gap-2 flex-wrap">
                          {account.canAccessCommissions && (
                            <span className="badge bg-success bg-opacity-10 text-success">
                              <DollarSign size={12} className="me-1" />
                              Commissions
                            </span>
                          )}
                          {account.canAccessPartnerships && (
                            <span className="badge bg-info bg-opacity-10 text-info">
                              <Link size={12} className="me-1" />
                              Partnerships
                            </span>
                          )}
                          {!account.canAccessCommissions && !account.canAccessPartnerships && (
                            <span className="text-muted">No extra permissions</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {account.isActive ? (
                          <span className="badge bg-success">
                            <CheckCircle size={12} className="me-1" />
                            Active
                          </span>
                        ) : (
                          <span className="badge bg-secondary">
                            <XCircle size={12} className="me-1" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {new Date(account.createdAt).toLocaleDateString()}
                        </small>
                      </td>
                      <td className="text-end">
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEditClick(account)}
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(account.id)}
                            title="Deactivate"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="modal show d-block"
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            overflow: 'auto'
          }}
          onClick={() => setShowCreateModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()} style={{ zIndex: 1051 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <UserPlus size={20} className="me-2" />
                  Create Sub-Account
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
              </div>
              <form onSubmit={handleCreateSubmit}>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6 mb-3 mb-md-0">
                      <label className="form-label fw-semibold">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                        name="firstName"
                        value={createForm.firstName}
                        onChange={handleCreateChange}
                        required
                      />
                      {errors.firstName && (
                        <div className="invalid-feedback">{errors.firstName}</div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                        name="lastName"
                        value={createForm.lastName}
                        onChange={handleCreateChange}
                        required
                      />
                      {errors.lastName && (
                        <div className="invalid-feedback">{errors.lastName}</div>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Email <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      name="email"
                      value={createForm.email}
                      onChange={handleCreateChange}
                      required
                    />
                    {errors.email && (
                      <div className="invalid-feedback">{errors.email}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Password <span className="text-danger">*</span>
                    </label>
                    <div className="position-relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                        name="password"
                        value={createForm.password}
                        onChange={handleCreateChange}
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ border: 'none', background: 'none' }}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {errors.password && (
                      <div className="invalid-feedback">{errors.password}</div>
                    )}
                    <small className="form-text text-muted">
                      Minimum 6 characters
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">WhatsApp Number <span className="text-danger">*</span></label>
                    <input
                      type="tel"
                      className="form-control"
                      name="phone"
                      value={createForm.phone}
                      onChange={handleCreateChange}
                      required
                      aria-invalid={!!errors.phone}
                      aria-describedby="create-whatsapp-help"
                    />
                    {errors.phone && <div className="text-danger small">{errors.phone}</div>}
                    <small id="create-whatsapp-help" className="form-text text-muted">Use this sub-agent’s own WhatsApp number. Missing country codes default to +234.</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Agency Name</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={mainAgentAgencyName}
                      disabled
                      readOnly
                    />
                    <small className="form-text text-muted">
                      Sub-accounts automatically use the main agent's agency name
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Extra Permissions</label>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="canAccessCommissions"
                        checked={createForm.canAccessCommissions}
                        onChange={handleCreateChange}
                        id="createCommissions"
                      />
                      <label className="form-check-label" htmlFor="createCommissions">
                        <DollarSign size={16} className="me-1" />
                        Access Commissions
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="canAccessPartnerships"
                        checked={createForm.canAccessPartnerships}
                        onChange={handleCreateChange}
                        id="createPartnerships"
                      />
                      <label className="form-check-label" htmlFor="createPartnerships">
                        <Link size={16} className="me-1" />
                        Access Partnerships
                      </label>
                    </div>
                    <small className="form-text text-muted">
                      By default, sub-accounts can only create and manage applications
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Save size={18} className="me-2" />
                        Create
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingAccount && (
        <div
          className="modal show d-block"
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            overflow: 'auto'
          }}
          onClick={() => setShowEditModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()} style={{ zIndex: 1051 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <Edit size={20} className="me-2" />
                  Edit Sub-Account
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6 mb-3 mb-md-0">
                      <label className="form-label fw-semibold">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                        name="firstName"
                        value={editForm.firstName}
                        onChange={handleEditChange}
                        required
                      />
                      {errors.firstName && (
                        <div className="invalid-feedback">{errors.firstName}</div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                        name="lastName"
                        value={editForm.lastName}
                        onChange={handleEditChange}
                        required
                      />
                      {errors.lastName && (
                        <div className="invalid-feedback">{errors.lastName}</div>
                      )}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Email <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      name="email"
                      value={editForm.email}
                      onChange={handleEditChange}
                      required
                    />
                    {errors.email && (
                      <div className="invalid-feedback">{errors.email}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      name="phone"
                      value={editForm.phone}
                      onChange={handleEditChange}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">New Password</label>
                    <div className="position-relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                        name="password"
                        value={editForm.password}
                        onChange={handleEditChange}
                        placeholder="Leave blank to keep current password"
                      />
                      <button
                        type="button"
                        className="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ border: 'none', background: 'none' }}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {errors.password && (
                      <div className="invalid-feedback">{errors.password}</div>
                    )}
                    <small className="form-text text-muted">
                      Leave blank to keep current password. Minimum 6 characters if changing.
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Confirm New Password</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                      name="confirmPassword"
                      value={editForm.confirmPassword}
                      onChange={handleEditChange}
                      placeholder="Confirm new password"
                    />
                    {errors.confirmPassword && (
                      <div className="invalid-feedback">{errors.confirmPassword}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Agency Name</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={mainAgentAgencyName}
                      disabled
                      readOnly
                    />
                    <small className="form-text text-muted">
                      Agency name cannot be changed for sub-accounts
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Status</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="isActive"
                        checked={editForm.isActive}
                        onChange={handleEditChange}
                        id="editIsActive"
                      />
                      <label className="form-check-label" htmlFor="editIsActive">
                        {editForm.isActive ? 'Active' : 'Inactive'}
                      </label>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Extra Permissions</label>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="canAccessCommissions"
                        checked={editForm.canAccessCommissions}
                        onChange={handleEditChange}
                        id="editCommissions"
                      />
                      <label className="form-check-label" htmlFor="editCommissions">
                        <DollarSign size={16} className="me-1" />
                        Access Commissions
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="canAccessPartnerships"
                        checked={editForm.canAccessPartnerships}
                        onChange={handleEditChange}
                        id="editPartnerships"
                      />
                      <label className="form-check-label" htmlFor="editPartnerships">
                        <Link size={16} className="me-1" />
                        Access Partnerships
                      </label>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={18} className="me-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

